import { Fragment, useCallback, useState } from 'react';

import { getRedirectUrls } from '@data/global-api-payments';
import {
  LiveRegionAnnouncer,
  useTranslationsContext,
} from '@state/checkout-contexts';
import { ChkCancelModal, ChkRadioButton } from '@ui/checkout-components';
import { UiButtonWithLoading } from '@ui/molecules-button-with-loading';
import { captureException } from '@utility/checkout-utils';
import { COMMON_EVENTS } from '@utility/data-layer';

import { useErrorContext } from '../../contexts/error/errorContext';
import type { CancellationReason } from '../../global.types';
import { TEST_IDS } from '../../models/globals/testIds.constants';
import { useScaSubject } from '../../providers/ScaSubjectProvider/ScaSubjectProvider.hooks';
import { dataLayer } from '../../utils/dataLayer';
import { getFlow } from '../../utils/getFlow/getFlow';
import { getStatus } from '../../utils/getStatus/gestStatus';
import { redirectOutsideSca } from '../../utils/redirectOutsideSca';
import { TRANSLATION_KEYS } from '../../utils/TranslationKeys';
import { HeaderWithCloseButton } from '../HeaderWithCloseButton/HeaderWithCloseButton';

import type { CancelSurveyProps } from './CancelSurvey.types';

export const CancelSurvey = ({
  isOpen,
  onClose,
  reasons,
  cancelScreenName,
}: CancelSurveyProps) => {
  const { setError } = useErrorContext();
  const { t } = useTranslationsContext();

  const [selectedValue, setSelectedValue] = useState<string | null>(null);
  const { cancel, isCancelFetching } = useScaSubject();

  const handleCancelPayment = async () => {
    const { result, error } = await cancel();

    if (error !== null || result === null) {
      return handleError(error);
    }

    const { directUrl } = result
      ? getRedirectUrls(getFlow(result))
      : { directUrl: null };

    if (!directUrl) {
      return handleError(new Error('Cancel return url not provided'));
    }

    dataLayer.trackEvent({
      name: COMMON_EVENTS.CANCEL_CLICK,
      properties: {
        cancelledScreen: `SCA ${cancelScreenName}`,
        feedback: selectedValue ?? '',
      },
    });

    redirectOutsideSca(directUrl, getStatus(result));
  };

  const handleClose = useCallback(() => {
    if (!isCancelFetching) {
      dataLayer.trackEvent({ name: COMMON_EVENTS.CANCEL_SKIP });
      onClose();
    }
  }, [onClose, isCancelFetching]);

  const handleRadioSelection = useCallback(
    (value: CancellationReason['value']) => {
      setSelectedValue(value);
    },
    []
  );

  const handleError = (error: unknown) => {
    if (error instanceof Error) {
      setError(error);
      captureException(error);
      dataLayer.trackEvent({
        name: COMMON_EVENTS.CANCEL_ERROR,
        properties: {
          code: error.name,
          message: error.message,
        },
      });
    } else {
      dataLayer.trackEvent({
        name: COMMON_EVENTS.CANCEL_ERROR,
        properties: { code: null },
      });
    }
  };

  const REASONS = reasons.map(({ label, value }, index) => {
    const isNotLastElement = index < reasons.length - 1;

    return (
      <Fragment key={label}>
        <ChkRadioButton
          size="lg"
          label={t(label)}
          value={value}
          onChange={() => handleRadioSelection(value)}
          checked={value === selectedValue}
          disabled={isCancelFetching}
        />
        {isNotLastElement && <span className="separator" />}
      </Fragment>
    );
  });

  const liveRegionSubmitMessage = isCancelFetching
    ? t(TRANSLATION_KEYS.SUBMITTING_CANCEL)
    : null;

  return (
    <ChkCancelModal.Wrapper open={isOpen} handleClose={handleClose}>
      <HeaderWithCloseButton
        onClick={handleClose}
        closeIconTestId={TEST_IDS.SCA_CANCEL_PAYMENT_CLOSE_BUTTON}
        aria-label={t(TRANSLATION_KEYS.CLOSE_CANCEL_MODAL)}
      >
        {t(TRANSLATION_KEYS.CANCEL_PAYMENT)}
      </HeaderWithCloseButton>
      <ChkCancelModal.BodyContainer>
        <ChkCancelModal.StyledBody>
          {t(TRANSLATION_KEYS.TO_IMPROVE_YOUR_NEXT_EXPERIENCE)}
        </ChkCancelModal.StyledBody>
        <ChkCancelModal.ReasonsGroup value={selectedValue}>
          {REASONS}
        </ChkCancelModal.ReasonsGroup>
        <LiveRegionAnnouncer
          debounceDelay={500}
          message={liveRegionSubmitMessage}
        />
        <UiButtonWithLoading
          onClick={handleCancelPayment}
          variant="contained"
          isLoading={isCancelFetching}
          disabled={isCancelFetching}
          data-testid={TEST_IDS.SCA_CANCEL_PAYMENT_CTA}
        >
          {t(TRANSLATION_KEYS.SUBMIT_AND_CANCEL_PAYMENT)}
        </UiButtonWithLoading>
      </ChkCancelModal.BodyContainer>
    </ChkCancelModal.Wrapper>
  );
};
